import { parseResolvedMarketChange } from "@/lib/marketChange";
import type { Product } from "@shared/types";

export type PricePoint = { price: number; recordedAt: number; saleCount: number };

// The image for each market shortcut comes from this product in the catalog.
export const MARKET_SHORTCUTS = [
  { slug: "apple", productSlug: "apple-iphone-duo" },
  { slug: "ipad", productSlug: "apple-ipad-pro-7th-generation" },
  { slug: "samsung", productSlug: "samsung-galaxy-s25-ultra" },
  { slug: "google", title: "Google Pixel", productSlug: "google-pixel-10-pro" },
  { slug: "playstation", productSlug: "sony-playstation-5-slim-digital-edition" },
  { slug: "xbox", productSlug: "microsoft-xbox-series-x" },
  { slug: "nintendo", productSlug: "nintendo-switch-2" },
  { slug: "vr", productSlug: "apple-vision-pro" },
  { slug: "headphones", productSlug: "apple-airpods-max" },
] as const;

// How each market reads in a headline ("VR headsets.") and in running text
// ("All VR headset models").
export const MARKET_NAMES: Record<string, { title: string; name: string }> = {
  apple: { title: "iPhone", name: "iPhone" },
  ipad: { title: "iPad", name: "iPad" },
  samsung: { title: "Samsung Galaxy", name: "Samsung Galaxy" },
  google: { title: "Google Pixel", name: "Google Pixel" },
  playstation: { title: "PlayStation", name: "PlayStation" },
  xbox: { title: "Xbox", name: "Xbox" },
  nintendo: { title: "Nintendo", name: "Nintendo" },
  vr: { title: "VR headsets", name: "VR headset" },
  headphones: { title: "Headphones", name: "Headphone" },
};

export const DEVICE_TYPE_LABELS: Record<Product["deviceType"], string> = {
  phones: "Phones",
  tablets: "Tablets",
  headphones: "Headphones",
  gameconsoles: "Game consoles",
  vr_headsets: "VR headsets",
};

const wholePriceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const monthYearFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

export const fullDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

export function getReleaseTimestamp(product: Pick<Product, "releaseDate">) {
  if (!product.releaseDate) return null;

  const parsed = Date.parse(product.releaseDate);
  return Number.isNaN(parsed) ? null : parsed;
}

export function getReleaseYear(product: Pick<Product, "releaseDate">) {
  const released = getReleaseTimestamp(product);
  return released == null ? null : new Date(released).getUTCFullYear();
}

// "2025 · Retail $1,199", or "Released Sep 2026" when there's no retail price.
export function getProductCardSubtitle(product: Product) {
  const releaseYear = getReleaseYear(product);
  if (product.retailPrice) {
    return [releaseYear, `Retail ${wholePriceFormatter.format(product.retailPrice)}`]
      .filter(part => part != null)
      .join(" · ");
  }

  const released = getReleaseTimestamp(product);
  return released == null ? "" : `Released ${monthYearFormatter.format(released)}`;
}

// Cheap devices swing on a handful of sales, so they'd crowd out every real move.
export const MOVERS_MIN_PRICE = 100;

// Catalog photos have white backgrounds: they blend into light surfaces, and in dark mode
// sit on a white plate instead.
export const PHOTO_PLATE_CLASS =
  "mix-blend-multiply dark:rounded-xl dark:bg-white dark:p-2 dark:mix-blend-normal";

export function getMarketChangeValue(product: Product) {
  const change = parseResolvedMarketChange(product.priceChangePercent24h);
  return change.hasResolvedChange ? change.value : null;
}

export function formatChange(value: number) {
  return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}%`;
}

export function getChangeColorClass(value: number) {
  return value >= 0
    ? "text-[#1A7F37] dark:text-emerald-400"
    : "text-[#D1242F] dark:text-rose-400";
}

export function getSalesCount(points: PricePoint[]) {
  return points.reduce((total, point) => total + point.saleCount, 0);
}

export function median(values: number[]) {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

// Daily sold prices swing with the mix of what sold, so lines follow a trailing median.
export function getTrailingMedians(prices: number[], windowSize: number) {
  return prices.map((_, index) =>
    median(prices.slice(Math.max(0, index - windowSize + 1), index + 1))
  );
}

export function getSortedPrices(points: PricePoint[]) {
  return points
    .filter(point => point.price > 0)
    .sort((a, b) => a.recordedAt - b.recordedAt);
}

export const SPARKLINE_WIDTH = 80;
export const SPARKLINE_HEIGHT = 24;

export function buildSparklinePath(points: PricePoint[]) {
  const sorted = getSortedPrices(points);
  if (sorted.length < 4) return null;

  const trend = getTrailingMedians(
    sorted.map(point => point.price),
    5
  );
  const low = Math.min(...trend);
  const high = Math.max(...trend);
  const padding = 2;

  return trend
    .map((value, index) => {
      const x = (index / (trend.length - 1)) * SPARKLINE_WIDTH;
      const y =
        high === low
          ? SPARKLINE_HEIGHT / 2
          : padding + (1 - (value - low) / (high - low)) * (SPARKLINE_HEIGHT - 2 * padding);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}
